import { useEffect, useRef, useState } from 'react';
import Breadcrumbs from '../../components/BreadCrumbs';
import CommanModal from '../../components/CommanModal';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchPopupStatusRequest,
  fetchRCIAccountRequest,
  fetchRCIAccountStatusRequest,
  fetchRewardAccountRequest,
  RootState,
  environment,
  initiateLinkCardRequest,
  linkSelectedAccountRequest,
  validateSelectedAccountRequest,
  useRecaptchaToken,
  generateOtpRequest,
  submitOtpRequest,
  resetBanLinkage,
  storeUserName,
  retrieveUserName,
  useProductName,
} from '@rb-serve-app/core';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

interface RciAccount {
  otpPhones: any;
  phones: any;
  accountLabel: string;
  accountNumber: string;
  autoValidation: boolean;
  address?: string;
  hasCtns?: boolean;
}

type CurrentScreenType =
  | 'INITIAL_SCREEN'
  | 'LOADING'
  | 'SELECT_ACCOUNT'
  | 'MOBILE_VERIFICATION'
  | 'OTP_VALIDATION'
  | 'IGNITE_SCREEN'
  | 'ACCOUNT_CONFIRMATION';

interface BannerMessageType {
  title: string | null;
  description: string | React.ReactNode | null;
  disableButton: boolean;
}

interface NoPhonesBannerType {
  showBanner: boolean;
  disableButton: boolean;
}

const TAG = 'LinkAccount';

/**
 * Returns React elements with superscript formatting for MD and ®
 */
const addSuperscriptJSX = (text: string): React.ReactNode => {
  if (!text) return '';
  const parts = text.split(/(MD|®)/g);
  return parts.map((part, index) => {
    if (part === 'MD' || part === '®') {
      return <sup key={index}>{part}</sup>;
    }
    return part;
  });
};

/**
 * Renders text with placeholders replaced, supporting JSX for superscripts
 */
const renderTextWithPlaceholders = (
  text: string,
  replacements: Record<string, React.ReactNode>
): React.ReactNode => {
  if (!text) return '';

  const placeholderRegex = /<[^>]+>/g;
  const parts = text.split(placeholderRegex);
  const matches = text.match(placeholderRegex) || [];

  const result: React.ReactNode[] = [];
  parts.forEach((part, index) => {
    result.push(part);
    if (matches[index]) {
      const replacement = replacements[matches[index]];
      result.push(replacement !== undefined ? replacement : matches[index]);
    }
  });

  return <>{result}</>;
};

const formatPhoneNumber = (phone: string): string => {
  if (!phone || phone.length !== 10) return phone;
  return `${phone.slice(0, 3)}-${phone.slice(3, 6)}-${phone.slice(6)}`;
};

const maskPhoneNumber = (phone: string): string => {
  if (!phone || phone.length !== 10) return phone;
  return `${phone.slice(0, 3)}*****${phone.slice(8)}`;
};

const maskedSelectedPhoneNumber = (phone: string) => {
  if (!phone) return '';
  const last4 = phone.slice(-4);
  return `+*******${last4}`;
};

function LinkAccount() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const getRecaptchaToken = useRecaptchaToken();
  const { productName } = useProductName();
  const lang = (localStorage.getItem('language') || 'en-US').split('-')[0];
  const { contentfulData } = useSelector(
    (state: { contentful: any }) => state.contentful
  );
  const { accountDetails, accountId, customerId, userName } = useSelector(
    (state: RootState) => state.user
  );
  const { status: popupStatus } = useSelector(
    (state: RootState) => state.popup
  );
  const replacePlaceholders = (
    text: string,
    replacements: Record<string, string>
  ): string => {
    if (!text) return '';
    let result = text;
    Object.entries(replacements).forEach(([placeholder, value]) => {
      result = result.replace(new RegExp(placeholder, 'g'), value || '');
    });
    return result;
  };
  const {
    linkingStatus,
    rciAccountStatus,
    rciAccountDetails,
    rewardAccountDetails,
    currentBan,
    otpVerified,
    loading,
    rciAccounts,
    linkScreenIndicator,
    error,
  } = useSelector((state: RootState) => state.banLinkage);

  const [currentScreen, setCurrentScreen] =
    useState<CurrentScreenType>('INITIAL_SCREEN');
  const [bannerMessage, setBannerMessage] = useState<BannerMessageType>({
    title: null,
    description: null,
    disableButton: false,
  });
  const [showBanner, setShowBanner] = useState<boolean>(false);
  const [showNoPhonesBanner, setShowNoPhonesBanner] =
    useState<NoPhonesBannerType>({
      showBanner: false,
      disableButton: false,
    });

  const initiatedLinkFlow = useRef<boolean>(false);
  const selectedAccountDetails = useRef<any>(null);
  const [selectedAccount, setSelectedAccount] = useState<RciAccount | null>(
    null
  );
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState<string | null>(
    null
  );
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [showResendBanner, setShowResendBanner] = useState(false);
  const [showAbandonFlowModal, setShowAbandonFlowModal] = useState(false);
  const [showTempUnavailableModal, setShowTempUnavailableModal] =
    useState(false);

  const { entries, assets } = contentfulData;
  const { rb_linkAccount, rb_links } = entries;
  const { rciBaseUrlWeb } = rb_links?.rbLinksCollection ?? {};
  const rbLinkUrl = rciBaseUrlWeb[environment?.env];

  const breadcrumbs = [
    { name: entries?.rb_navigation?.homeLogoTitle, href: '/' },
    { name: entries?.rb_navigation?.rewardsLogoTitle, href: '/rewards' },
    {
      name: rb_linkAccount?.linkAccountLandingPage?.title,
      href: '/link-account',
    },
  ];

  useEffect(() => {
    console.log('Error in LinkAccount:', error);
  }, [error]);

  useEffect(() => {
    const strippeduser_id = accountId.replace(/^0+/, '') || '0';
    if (popupStatus && !popupStatus?.showActivateCardPopup) {
      const rciAccountDetailsApi =
        Object.keys(rciAccountDetails || {}).length > 0;
      const rewardAccountDetailsApi =
        Object.keys(rewardAccountDetails || {}).length > 0;
      const rciAccountStatusApi =
        Object.keys(rciAccountStatus || {}).length > 0;
      if (!rewardAccountDetailsApi) {
        dispatch(
          fetchRewardAccountRequest({
            user_id: strippeduser_id,
          })
        );
      }
      if (!rciAccountStatusApi) {
        dispatch(
          fetchRCIAccountStatusRequest({
            accountId: accountId,
          })
        );
      }
      if (!rciAccountDetailsApi) {
        dispatch(
          fetchRCIAccountRequest({
            accountId: accountId,
          })
        );
      }
    }
    if (accountId && (!popupStatus || Object.keys(popupStatus).length === 0)) {
      dispatch(fetchPopupStatusRequest({ accountId, customerId }));
    }
  }, [
    accountId,
    popupStatus,
    customerId,
    dispatch,
    rewardAccountDetails,
    rciAccountDetails,
    rciAccountStatus,
  ]);

  useEffect(() => {
    if (popupStatus && popupStatus?.showActivateCardPopup) {
      setBannerMessage({
        title: '',
        description: rb_linkAccount?.bannerMsgs?.activateCardDescription || '',
        disableButton: true,
      });
    }
  }, [popupStatus, rb_linkAccount?.bannerMsgs?.activateCardDescription]);

  useEffect(() => {
    console.log(TAG + '_Account_Details', accountDetails);
    console.log(TAG + '_Popup_Status', popupStatus);
    console.log(TAG + '_Linking_Status', linkingStatus);
    console.log(TAG + '_RCI_Account_Status', rciAccountStatus);
    console.log(TAG + '_RCI_Account_Details', rciAccountDetails);
    console.log(TAG + '_Reward_Account_Details', rewardAccountDetails);
    console.log(TAG + '_Current_Ban', currentBan);

    if (rciAccountDetails?.bankAccountResponse && !showBanner) {
      console.log(TAG + 'Showing Banner');
      const linkageStatus =
        rciAccountDetails?.bankAccountResponse?.linkageStatus;
      const linkageDate = rciAccountDetails?.bankAccountResponse?.linkageDate;

      if (
        rewardAccountDetails &&
        typeof rewardAccountDetails === 'object' &&
        rewardAccountDetails.statusCode === 400 &&
        rewardAccountDetails.title === '-10'
      ) {
        const notInGoodStandingMsg = renderTextWithPlaceholders(
          rb_linkAccount?.bannerMsgs?.notInGoodStanding ||
            'You cannot link your accounts at this time due to your <card name> account status. Please call the number on the back of your card for assistance.',
          {
            '<card name>': addSuperscriptJSX(productName) || '',
          }
        );
        setBannerMessage({
          title: '',
          description: notInGoodStandingMsg,
          disableButton: true,
        });
        return;
      }

      // Already linked to a Rogers wireless plan
      if (linkageStatus === '1') {
        setBannerMessage({
          title: '',
          description:
            rb_linkAccount?.bannerMsgs?.alreadyConnected ||
            'Your account is already linked to a Rogers wireless plan.',
          disableButton: true,
        });
        setShowBanner(true);
        return;
      }

      // Derogatory alt: account not in good standing and not linked (accountStatus !== 1 && linkageStatus === '4')
      const accountLinkStatus =
        rewardAccountDetails?.redeemerProfile?.accountInfo?.accountStatus;
      if (accountLinkStatus !== '1' && linkageStatus === '4') {
        const derogatoryAltMsg = renderTextWithPlaceholders(
          rb_linkAccount?.bannerMsgs?.notInGoodStanding ||
            'You cannot link your accounts at this time due to your <card name> account status. Please call the number on the back of your card for assistance.',
          {
            '<card name>': addSuperscriptJSX(productName) || '',
          }
        );
        setBannerMessage({
          title: '',
          description: derogatoryAltMsg,
          disableButton: true,
        });
        setShowBanner(true);
        return;
      }

      // Check for cooldown period (linkageStatus === '4')
      if (linkageStatus === '4' && linkageDate) {
        const cooldownPeriod = rb_linkAccount?.config?.coolDownPeriod;

        // If cooldownPeriod is 0, negative, undefined, or null - skip cooldown banner (no restriction)
        if (
          cooldownPeriod === undefined ||
          cooldownPeriod === null ||
          cooldownPeriod <= 0
        ) {
          // No cooldown restriction - allow linking immediately
          return;
        }

        const nextLinkageDate = moment(linkageDate).add(cooldownPeriod, 'days');

        // Only show banner if the cooldown period hasn't expired yet
        if (nextLinkageDate.isAfter(moment())) {
          const formattedDate = nextLinkageDate
            .locale(lang?.includes('fr') ? 'fr' : 'en')
            .format('YYYY-MM-DD');
          const cooldownMessage = (
            rb_linkAccount?.bannerMsgs?.cooldownPeriodDesc ||
            'You unlinked your credit card account from your Rogers Account. You can link your account again after %s'
          ).replace('%s', formattedDate);

          setBannerMessage({
            title: '',
            description: cooldownMessage,
            disableButton: true,
          });
          setShowBanner(true);
        }
        return;
      }
    }
  }, [
    rciAccountDetails,
    accountDetails,
    rewardAccountDetails,
    currentBan,
    popupStatus,
    rb_linkAccount,
    linkingStatus,
    rciAccountStatus,
    showBanner,
    lang,
    productName,
  ]);
  const productName1 = accountDetails?.productName;
  const getFormattedDescription = (): string => {
    return formatRoamLikeHomeText(
      entries?.rb_roam_like_home?.roamLikeHomeContent?.description,
      entries?.rb_roam_like_home?.rbRoamLikeHomeDays,
      productName1
    );
  };
  const getRoamLikeHomeDays = (
    rbRoamLikeHomeDays: Record<string, number> | undefined,
    productName1: string | undefined
  ): number => {
    if (!rbRoamLikeHomeDays || !productName1) {
      return 5; // Default value
    }
    return rbRoamLikeHomeDays[productName1] || 5;
  };
  const formatRoamLikeHomeText = (
    text: string | undefined,
    rbRoamLikeHomeDays: Record<string, number> | undefined,
    productName1: string | undefined
  ): string => {
    if (!text) return '';

    const days = getRoamLikeHomeDays(rbRoamLikeHomeDays, productName1);
    return text.replace('%s', days.toString());
  };

  useEffect(() => {
    return () => {
      setShowBanner(false);
      setBannerMessage({
        title: null,
        description: null,
        disableButton: false,
      });
      setShowNoPhonesBanner({
        showBanner: false,
        disableButton: false,
      });
    };
  }, []);

  useEffect(() => {
    if (bannerMessage.description) {
      setShowBanner(true);
      console.log(TAG + '_Banner_Message', bannerMessage);
    }
  }, [bannerMessage]);

  useEffect(() => {
    // App code logic,
    const searchParams = new URLSearchParams(window.location.search);
    const appCode = searchParams.get('appCode');
    const rciAccountStatusApi = Object.keys(rciAccountStatus || {}).length > 0;
    const rciAccountDetailsApi =
      Object.keys(rciAccountDetails || {}).length > 0;
    const rewardAccountDetailsApi =
      Object.keys(rewardAccountDetails || {}).length > 0;

    // Retrieve userName from localStorage when returning with appCode
    if (appCode) {
      dispatch(retrieveUserName());
    }

    if (
      appCode &&
      accountId &&
      customerId &&
      rciAccountDetailsApi &&
      rciAccountStatusApi &&
      rewardAccountDetailsApi
    ) {
      console.log(TAG + '_Initiating_Link_Card_Request', appCode);
      initiatedLinkFlow.current = true;
      dispatch(
        initiateLinkCardRequest({
          accountId,
          customerId,
          appCode,
        })
      );
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [
    accountId,
    customerId,
    rciAccountDetails,
    rciAccountStatus,
    rewardAccountDetails,
    dispatch,
  ]);

  useEffect(() => {
    if (
      linkScreenIndicator === 'LINKED_ALREADY' &&
      initiatedLinkFlow.current &&
      !rciAccounts
    ) {
      console.log(TAG + '_Link_Screen_Indicator', linkScreenIndicator);
      dispatch(resetBanLinkage());
      navigate('/rewards', { state: { linkScreenIndicator } });
      return;
    }
    if (
      rciAccounts &&
      initiatedLinkFlow.current &&
      !showNoPhonesBanner.showBanner &&
      linkScreenIndicator
    ) {
      console.log(TAG + '_Link_Screen_Indicator', linkScreenIndicator);

      if (rciAccounts && linkScreenIndicator === 'LINK_ACCOUNT_SELECTION') {
        setCurrentScreen('SELECT_ACCOUNT');
        return;
      }

      if (rciAccounts && linkScreenIndicator === 'LINK_OTP_PHONE_SELECTION') {
        selectedAccountDetails.current = rciAccounts?.[0] || null;
        setCurrentScreen('MOBILE_VERIFICATION');
        return;
      }

      if (
        rciAccounts &&
        linkScreenIndicator === 'LINK_OTP_VERIFICATION_SCREEN'
      ) {
        selectedAccountDetails.current = rciAccounts?.[0] || null;
        setCurrentScreen('OTP_VALIDATION');
        return;
      }

      if (rciAccounts && linkScreenIndicator === 'LINK_ACCOUNT_CONFIRMATION') {
        selectedAccountDetails.current = rciAccounts?.[0] || null;
        setCurrentScreen('ACCOUNT_CONFIRMATION');
        return;
      }

      if (rciAccounts && linkScreenIndicator === 'LINK_ACCOUNT_NOT_VERIFIED') {
        setCurrentScreen('INITIAL_SCREEN');
        setShowNoPhonesBanner({
          showBanner: true,
          disableButton: false,
        });
        return;
      }

      if (rciAccounts && linkScreenIndicator === 'LINK_ALMOST_THERE') {
        console.log(TAG + '_Link_Screen_Indicator', linkScreenIndicator);
        dispatch(resetBanLinkage());
        navigate('/rewards', { state: { linkScreenIndicator } });
        return;
      }

      if (
        rciAccounts &&
        (linkScreenIndicator === 'LINK_TRUE' ||
          linkScreenIndicator === 'LINK_FALSE' ||
          linkScreenIndicator === 'LINK_NOT_ELIGIBLE' ||
          linkScreenIndicator === 'NOT_ELIGIBLE')
      ) {
        console.log(TAG + '_Link_Screen_Indicator', linkScreenIndicator);
        dispatch(resetBanLinkage());
        navigate('/rewards', { state: { linkScreenIndicator } });
        return;
      }

      if (
        linkScreenIndicator === 'LINKED_ALREADY' &&
        initiatedLinkFlow.current
      ) {
        dispatch(resetBanLinkage());
        navigate('/rewards', { state: { linkScreenIndicator } });
        return;
      }

      if (
        linkScreenIndicator === 'TEMP_UNAVAILABLE' &&
        initiatedLinkFlow.current
      ) {
        console.log(TAG + '_Link_Screen_Indicator', linkScreenIndicator);
        setShowTempUnavailableModal(true);
        return;
      }
    }
  }, [
    initiatedLinkFlow,
    rciAccounts,
    linkScreenIndicator,
    showNoPhonesBanner,
    navigate,
    dispatch,
  ]);

  // Handle browser back button when link flow is initiated
  useEffect(() => {
    if (initiatedLinkFlow.current) {
      // Push a state to history so we can detect back button
      window.history.pushState({ linkFlowActive: true }, '');

      const handlePopState = (event: PopStateEvent) => {
        if (initiatedLinkFlow.current) {
          // Prevent default navigation and show modal
          window.history.pushState({ linkFlowActive: true }, '');
          setShowAbandonFlowModal(true);
        }
      };

      window.addEventListener('popstate', handlePopState);

      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [initiatedLinkFlow.current]);

  const handleAbandonFlowContinue = () => {
    setShowAbandonFlowModal(false);
  };

  const handleAbandonFlowStop = () => {
    setShowAbandonFlowModal(false);
    initiatedLinkFlow.current = false;
    dispatch(resetBanLinkage());
    navigate('/rewards');
  };

  const handleTempUnavailableOk = () => {
    setShowTempUnavailableModal(false);
    initiatedLinkFlow.current = false;
    dispatch(resetBanLinkage());
    navigate('/rewards');
  };

  const handleStartLinkAccountFlow = () => {
    // Store userName to localStorage before navigating to external URL
    if (userName) {
      dispatch(storeUserName(userName));
    }
    console.log('Link Account URL:', rbLinkUrl);
    window.open(rbLinkUrl, '_self');
  };

  const handleLinkAccVerification = () => {
    dispatch(
      linkSelectedAccountRequest({
        accountId,
        customerId,
        ban: selectedAccountDetails.current?.accountNumber || '',
      })
    );
  };

  const handleVerifySelectedAcc = () => {
    dispatch(
      validateSelectedAccountRequest({
        ban: selectedAccount?.accountNumber || '',
        accountId,
        customerId,
        rciAccount: selectedAccount,
      })
    );
  };

  const handleGenerateOTP = async () => {
    try {
      const token = await getRecaptchaToken('banlink_generate_otp');
      dispatch(
        generateOtpRequest({
          accountId,
          mobileNumber: selectedPhoneNumber?.startsWith('+')
            ? selectedPhoneNumber
            : `+1${selectedPhoneNumber}`,
          language: lang,
          recaptchaToken: token ?? '',
        })
      );
    } catch (error) {
      console.log('Generate otp error:', error);
    }
  };

  const handleSubmitOTP = async () => {
    try {
      const token = await getRecaptchaToken('banlink_submit_otp');
      dispatch(
        submitOtpRequest({
          accountId,
          mobileNumber: selectedPhoneNumber?.startsWith('+')
            ? selectedPhoneNumber
            : `+1${selectedPhoneNumber}`,
          otp: verificationCode,
          recaptchaToken: token ?? '',
        })
      );
    } catch (error) {
      console.log('Submit otp error:', error);
    }
  };

  return (
    <main role="main" className="bg-bg-body py-4 md:px-8 px-4">
      <nav aria-label="Breadcrumb">
        <Breadcrumbs items={breadcrumbs} />
      </nav>
      <section
        className="w-full rounded-[15px] bg-white min-h-[70vh] flex-1 flex-col md:p-6 p-2"
        aria-labelledby="roam-like-home-title"
      >
        <header className="mb-6">
          {showBanner && (
            <div className="bg-[#FFF6F3] flex items-start p-4 space-x-3 mb-5 border-l-[6px] border-red-500">
              <img
                src={assets?.accNotEligibleIcon}
                alt="Error Icon"
                className="w-6 h-6 mt-1"
              />
              <div className="">
                {/* <p className="font-semibold text-[#1F1F1F] text-base">
                  {bannerMessage.title}
                </p> */}
                <p className="mt-1 text-[#1F1F1F] text-base">
                  {bannerMessage.description}
                </p>
              </div>
            </div>
          )}
          <h1
            id="roam-like-home-title"
            className="text-[22px] font-semibold leading-[28px] tracking-[0.35%] text-[#1F1F1F]"
          >
            {rb_linkAccount?.linkAccountLandingPage?.title}
          </h1>
        </header>
        {currentScreen === 'INITIAL_SCREEN' && (
          <div className="mx-auto max-w-lg bg-white rounded-lg shadow-lg py-5 px-8 text-center border border-gray-200">
            {showNoPhonesBanner.showBanner && (
              <div className="bg-[#FFF6F3] flex items-center p-4 space-x-3 mb-5">
                <img
                  src={assets?.we_are_sorry}
                  alt="Error Icon"
                  className="w-8 h-8"
                />
                <div className="text-base font-normal text-left">
                  <span className="text-[#AE2116]">
                    {rb_linkAccount?.bannerMsgs?.accNotVerified}
                  </span>
                  {/* <span className="text-blue-400">
                    {rb_linkAccount?.bannerMsgs?.number}
                  </span>
                  <span className="text-[#AE2116]">
                    {rb_linkAccount?.bannerMsgs?.accNotVerified2}
                  </span>
                  <span className="text-blue-400">
                    {rb_linkAccount?.bannerMsgs?.here}
                  </span> */}
                </div>
              </div>
            )}
            <div className="flex flex-col items-center mt-5 mb-5">
              <div className="flex justify-center items-center space-x-5 mb-5">
                <img
                  src={assets?.card_icon}
                  alt="Card Icon"
                  className="w-10 h-12"
                />
                <img
                  src={assets?.link_icn}
                  alt="Link Icon"
                  className="w-8 h-7"
                />
                <img
                  src={assets?.icn_home}
                  alt="Home Icon"
                  className="w-12 h-12"
                />
              </div>
              <span className="text-[22px] font-semibold text-[#1F1F1F] mb-12">
                {rb_linkAccount?.linkAccountLandingPage?.header}
              </span>
            </div>
            <p className="text-base text-[#000] mb-12">
              {getFormattedDescription()}
            </p>
            <p className="text-base mb-12">
              {rb_linkAccount?.linkAccountLandingPage?.rogersAcc}
              <a
                href="https://www.rogers.com/customerSupport/contUsForm"
                onClick={() => null}
                rel="noopener noreferrer"
                className="text-[#DA291C] cursor-pointer"
              >
                {rb_linkAccount?.linkAccountLandingPage?.signUpText}
              </a>
            </p>
            <button
              onClick={handleStartLinkAccountFlow}
              disabled={bannerMessage?.disableButton}
              className={`relative inline-flex items-center justify-center w-52 h-12  text-base font-semibold ${
                bannerMessage?.disableButton
                  ? 'bg-gray-100 text-gray-500 border border-gray-300'
                  : 'bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 '
              }`}
            >
              {loading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="loader ease-linear rounded-full border-4 border-t-4 border-white h-6 w-6"></div>
                </div>
              )}
              <div className={`${loading ? 'invisible' : 'flex items-center'}`}>
                {rb_linkAccount?.linkAccountLandingPage?.linkAcc}
              </div>
            </button>
          </div>
        )}
        {currentScreen === 'SELECT_ACCOUNT' && (
          <div className="p-8 max-w-lg mx-auto border border-gray-200 rounded-lg shadow-lg bg-white text-center">
            <div className="flex justify-center items-center space-x-4 mb-6">
              <img
                src={assets?.card_icon}
                alt="Card Icon"
                className="w-8 h-10"
              />
              <img src={assets?.link_icn} alt="Link Icon" className="w-7 h-6" />
              <img
                src={assets?.icn_home}
                alt="Home Icon"
                className="w-10 h-10"
              />
            </div>
            <h2 className="text-xl font-semibold mb-7 text-[#1F1F1F]">
              {rb_linkAccount?.linkAccSelections?.accSelectionHeader}
            </h2>
            {/* <p className="mb-5 text-base text-[#1F1F1F] font-normal">
              {rb_linkAccount?.linkAccSelections?.accSelectionDesc}
            </p> */}
            {rciAccounts?.map((acc: RciAccount) => {
              const isSelected =
                selectedAccount?.accountNumber === acc.accountNumber;
              return (
                <div
                  key={acc.accountNumber}
                  onClick={() => setSelectedAccount(acc)}
                  className={`cursor-pointer text-left mb-4 p-4 rounded-lg shadow-sm border ${
                    isSelected
                      ? 'border-[#DA291C] bg-[#FFF0F0]'
                      : 'border-gray-200'
                  }`}
                >
                  <div
                    className={`text-xs ${isSelected ? 'text-[#DA291C]' : 'text-gray-500'} font-medium text-right`}
                  >
                    {isSelected
                      ? rb_linkAccount?.linkAccSelections?.select
                      : rb_linkAccount?.linkAccSelections?.clickToSelect}
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-semibold text-base text-[#1F1F1F]">
                        {rb_linkAccount?.linkAccSelections?.account}{' '}
                        {acc.accountNumber}
                      </p>

                      {acc?.hasCtns && (
                        <ul className="mt-2 text-base text-[#1F1F1F] text-left">
                          {acc.phones.map((phone: string, i: number) => (
                            <li key={i}>{formatPhoneNumber(phone)}</li>
                          ))}
                        </ul>
                      )}

                      {acc?.address && !acc?.hasCtns && (
                        <span className="text-gray-700 mt-1 text-sm">
                          {acc?.address}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            <button
              onClick={() => {
                handleVerifySelectedAcc();
              }}
              disabled={!selectedAccount || loading}
              className={`relative inline-flex items-center justify-center px-4 py-3 w-52 text-white text-base font-semibold ${
                selectedAccount && !loading
                  ? 'bg-[#DA291C] hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2'
                  : 'bg-gray-300 cursor-not-allowed'
              }`}
            >
              {loading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="loader ease-linear rounded-full border-4 border-t-4 border-white h-6 w-6"></div>
                </div>
              )}
              <span className={loading ? 'invisible' : ''}>
                {rb_linkAccount?.linkAccSelections?.next}
              </span>
            </button>
          </div>
        )}
        {currentScreen === 'MOBILE_VERIFICATION' && selectedAccountDetails && (
          <div className="p-8 max-w-lg mx-auto border border-gray-200 rounded-lg shadow-lg bg-white text-center">
            <div className="flex justify-center items-center space-x-4 mb-7">
              <img
                src={assets?.card_icon}
                alt="Card Icon"
                className="w-8 h-10"
              />
              <img src={assets?.link_icn} alt="Link Icon" className="w-7 h-6" />
              <img
                src={assets?.icn_home}
                alt="Home Icon"
                className="w-10 h-10"
              />
            </div>
            <h2 className="text-xl font-semibold mb-6 text-[#1F1F1F]">
              {rb_linkAccount?.linkAccSelections?.oneStepText}
            </h2>
            <p className="mb-6 text-base text-[#000]">
              {rb_linkAccount?.linkAccSelections?.otpFlowDesc}{' '}
              {selectedAccountDetails.current?.accountNumber}{' '}
              {rb_linkAccount?.linkAccSelections?.otpFlowDesc1}
            </p>
            <p className="text-base text-[#000] mb-3">
              {rb_linkAccount?.linkAccSelections?.otpFlowDesc2}
            </p>
            <div className="space-y-4 mb-8">
              {selectedAccountDetails.current?.otpPhones?.map(
                (num: any, index: any) => (
                  <div
                    key={index}
                    className="flex items-left justify-left text-left border-b px-6 py-4"
                  >
                    <input
                      type="radio"
                      id={`phone-${index}`}
                      name="phoneNumber"
                      value={num}
                      checked={selectedPhoneNumber === num}
                      onChange={(e) => setSelectedPhoneNumber(e.target.value)}
                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 accent-[#DA291C]"
                    />
                    <label
                      htmlFor={`phone-${index}`}
                      className="ml-3 text-base text-[#1F1F1F] font-normal"
                    >
                      {maskPhoneNumber(num)}
                    </label>
                  </div>
                )
              )}
            </div>
            <button
              onClick={() => {
                if (selectedPhoneNumber) {
                  handleGenerateOTP();
                }
              }}
              disabled={!selectedPhoneNumber || loading}
              className={`relative inline-flex items-center justify-center px-4 py-3 w-52 text-white text-base font-semibold ${
                selectedPhoneNumber && !loading
                  ? 'bg-[#DA291C] hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2'
                  : 'bg-gray-300 cursor-not-allowed'
              }`}
            >
              {loading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="loader ease-linear rounded-full border-4 border-t-4 border-white h-6 w-6"></div>
                </div>
              )}
              <span className={loading ? 'invisible' : ''}>
                {rb_linkAccount?.linkAccSelections?.sendCode}
              </span>
            </button>
          </div>
        )}
        {currentScreen === 'OTP_VALIDATION' && selectedPhoneNumber && (
          <div className="p-8 max-w-lg mx-auto border border-gray-200 rounded-lg shadow-lg bg-white text-center">
            {showResendBanner && (
              <div className="bg-[#F4FCF7] flex items-center p-4 space-x-3 mb-5">
                <img
                  src={assets?.tickIcon}
                  alt="tickIcon"
                  className="w-8 h-8"
                />
                <div className="text-base font-normal text-left">
                  <span className="text-[#003D46]">
                    {rb_linkAccount?.bannerMsgs?.resendMsg}
                  </span>
                </div>
              </div>
            )}
            <div className="flex justify-center items-center space-x-4 mb-5">
              <img
                src={assets?.card_icon}
                alt="Card Icon"
                className="w-8 h-10"
              />
              <img src={assets?.link_icn} alt="Link Icon" className="w-7 h-6" />
              <img
                src={assets?.icn_home}
                alt="Home Icon"
                className="w-10 h-10"
              />
            </div>
            <h2 className="text-xl font-semibold mb-5 text-[#1F1F1F]">
              {rb_linkAccount?.linkAccSelections?.enterCode}
            </h2>
            <p className="mb-4 text-base text-[#000] font-normal">
              {rb_linkAccount?.linkAccSelections?.enterCodeFlowDesc}{' '}
              <span className="font-semibold">
                {maskedSelectedPhoneNumber(selectedPhoneNumber)}
              </span>
              {/* {' '} */}
              {/* {rb_linkAccount?.linkAccSelections?.enterCodeFlowDesc1} */}
            </p>
            {/* <p className="text-base text-[#000] mb-4">
              {rb_linkAccount?.linkAccSelections?.enterCodeFlowDesc2}
            </p> */}
            <div className="mb-6">
              <label
                htmlFor="verification-code"
                className="block text-left text-base font-normal text-[#1F1F1F] mb-2"
              >
                {rb_linkAccount?.linkAccSelections?.verificationCode}
              </label>
              <input
                type="text"
                id="verification-code"
                name="verificationCode"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                className="block w-full px-4 py-3 border border-gray-300 shadow-sm focus:outline-none sm:text-base"
                placeholder="Enter code"
              />
            </div>
            <div className="flex justify-between items-center mb-6">
              <p className="text-base text-[#000] font-normal">
                {rb_linkAccount?.linkAccSelections?.didnotReceive}
              </p>
              <button
                onClick={() => {
                  handleGenerateOTP();
                  setShowResendBanner(true);
                  setTimeout(() => setShowResendBanner(false), 5000);
                }}
                disabled={loading}
                className={`relative py-2 px-4 border text-base font-medium focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 ${
                  loading
                    ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                    : 'border-[#DA291C] text-[#DA291C] hover:bg-red-50'
                }`}
              >
                {rb_linkAccount?.linkAccSelections?.resend}
              </button>
            </div>
            <button
              onClick={() => handleSubmitOTP()}
              disabled={verificationCode.length < 6 || loading}
              className={`relative inline-flex items-center justify-center px-4 py-3 w-52 text-white text-base font-semibold ${
                verificationCode.length === 6 && !loading
                  ? 'bg-[#DA291C] hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2'
                  : 'bg-gray-300 cursor-not-allowed'
              }`}
            >
              {loading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="loader ease-linear rounded-full border-4 border-t-4 border-white h-6 w-6"></div>
                </div>
              )}
              <span className={loading ? 'invisible' : ''}>
                {rb_linkAccount?.linkAccSelections?.submit}
              </span>
            </button>
          </div>
        )}
        {currentScreen === 'ACCOUNT_CONFIRMATION' &&
          selectedAccountDetails.current && (
            <div className="p-8 max-w-lg mx-auto border border-gray-200 rounded-lg shadow-lg bg-white text-center">
              <div className="flex flex-col justify-center items-center mb-7">
                {otpVerified ? (
                  <>
                    <img
                      src={assets?.icon_success}
                      alt="successIcon"
                      className="w-12 h-12 mb-2"
                    />
                    <p className="text-lg font-normal text-[#1F1F1F]">
                      {rb_linkAccount?.linkAccSelections?.verified}
                    </p>
                  </>
                ) : (
                  <>
                    <img
                      src={assets?.card_icon}
                      alt="Card Icon"
                      className="w-8 h-10"
                    />
                    <img
                      src={assets?.link_icn}
                      alt="Link Icon"
                      className="w-7 h-6"
                    />
                    <img
                      src={assets?.icn_home}
                      alt="Home Icon"
                      className="w-10 h-10"
                    />
                  </>
                )}
              </div>
              <h2 className="text-xl font-semibold mb-6 text-[#1F1F1F]">
                {rb_linkAccount?.linkAccSelections?.account}
                {selectedAccountDetails.current?.accountNumber}
              </h2>
              <p className="mb-5 text-base text-[#000]">
                {renderTextWithPlaceholders(
                  rb_linkAccount?.linkAccSelections?.verifiedDesc,
                  {
                    '<card name>': addSuperscriptJSX(productName) || '',
                  }
                )}
              </p>
              {rciAccounts?.map((acc: RciAccount) => {
                return (
                  <ul className="mb-6 text-base text-[#1F1F1F] text-center">
                    {acc.phones.map((phone: string, i: number) => (
                      <li key={i}>{formatPhoneNumber(phone)}</li>
                    ))}
                  </ul>
                );
              })}
              <button
                onClick={() => handleLinkAccVerification()}
                disabled={loading}
                className={`relative inline-flex items-center justify-center px-4 py-3 w-52 text-white text-base font-semibold ${
                  loading
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2'
                }`}
              >
                {loading && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="loader ease-linear rounded-full border-4 border-t-4 border-white h-6 w-6"></div>
                  </div>
                )}
                <span className={loading ? 'invisible' : ''}>
                  {rb_linkAccount?.linkAccountLandingPage?.linkAcc}
                </span>
              </button>
            </div>
          )}
        {currentScreen === 'IGNITE_SCREEN' &&
          selectedAccountDetails.current && (
            <div className="p-8 max-w-lg mx-auto border border-gray-200 rounded-lg shadow-lg bg-white text-center">
              <div className="flex justify-center items-center space-x-4 mb-7">
                <img
                  src={assets?.card_icon}
                  alt="Card Icon"
                  className="w-8 h-10"
                />
                <img
                  src={assets?.link_icn}
                  alt="Link Icon"
                  className="w-7 h-6"
                />
                <img
                  src={assets?.icn_home}
                  alt="Home Icon"
                  className="w-10 h-10"
                />
              </div>
              <h2 className="text-xl font-semibold mb-6 text-[#1F1F1F]">
                {rb_linkAccount?.linkAccSelections?.account}
                {selectedAccountDetails.current?.accountNumber}
              </h2>
              <p className="mb-1 text-base text-[#000]">
                {renderTextWithPlaceholders(
                  rb_linkAccount?.linkAccSelections?.igniteDesc,
                  {
                    '<card name>': addSuperscriptJSX(productName) || '',
                  }
                )}
              </p>
              <h2 className="text-xl font-semibold mb-1 text-[#1F1F1F]">
                {rb_linkAccount?.linkAccSelections?.residential}
              </h2>
              <p className="mb-5 text-base text-[#000]">
                {selectedAccountDetails.current?.address}
              </p>
              <button
                onClick={handleLinkAccVerification}
                disabled={loading}
                className={`relative inline-flex items-center justify-center px-4 py-3 w-52 text-white text-base font-semibold ${
                  loading
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2'
                }`}
              >
                {loading && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="loader ease-linear rounded-full border-4 border-t-4 border-white h-6 w-6"></div>
                  </div>
                )}
                <span className={loading ? 'invisible' : ''}>
                  {rb_linkAccount?.linkAccountLandingPage?.linkAcc}
                </span>
              </button>
            </div>
          )}
      </section>

      {/* Abandon Flow Modal */}
      <CommanModal
        isOpen={showAbandonFlowModal}
        onClose={handleAbandonFlowContinue}
        closeIcon={false}
        buttonText={
          rb_linkAccount?.modal?.abandonFlowModal?.cta1 || 'Continue linking'
        }
        extraButtonText={
          rb_linkAccount?.modal?.abandonFlowModal?.cta2 || 'Stop'
        }
        onExtraButtonClick={handleAbandonFlowStop}
        buttonClassName="flex-col gap-3 mt-6"
        width="max-w-md"
        padding="p-8"
        buttonstyle="primary"
        extrabuttonstyle="secondary"
        jsxContent={
          <div className="text-center">
            <h2 className="text-xl font-semibold text-[#1F1F1F] mb-4">
              {rb_linkAccount?.modal?.abandonFlowModal?.title ||
                'Are you sure you want to stop linking your accounts?'}
            </h2>
            <p className="text-base text-[#1F1F1F]">
              {rb_linkAccount?.modal?.abandonFlowModal?.description ||
                "If you stop linking now you won't receive exclusive Rogers benefits like 5 Roam Like Home days."}
            </p>
          </div>
        }
      />

      {/* Temporarily Unavailable Modal */}
      <CommanModal
        isOpen={showTempUnavailableModal}
        onClose={handleTempUnavailableOk}
        closeIcon={false}
        buttonText={rb_linkAccount?.modal?.tempUnavailableModal?.cta || 'Ok'}
        buttonClassName="flex-col gap-3 mt-6"
        width="max-w-md"
        padding="p-8"
        jsxContent={
          <div className="text-center">
            <h2 className="text-xl font-semibold text-[#1F1F1F] mb-4">
              {rb_linkAccount?.modal?.tempUnavailableModal?.title ||
                'Temporarily unavailable'}
            </h2>
            <p className="text-base text-[#1F1F1F]">
              {rb_linkAccount?.modal?.tempUnavailableModal?.description ||
                'Sorry for the inconvenience, we are unable to update the requested changes. Please try again later.'}
            </p>
          </div>
        }
      />
    </main>
  );
}

export default LinkAccount;
